import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldAlert } from "lucide-react";
import DashboardLayout from "../../layouts/DashboardLayout";
import useFeatures from "../../hooks/useFeatures";
import api from "../../api";

// Risk register (backend clinical/workspace_routes.py): patients whose
// newest risk assessment is moderate or high, with what still needs doing.
// Clinicians see their own care-team patients; supervisors add their team's;
// admins see everyone (risk level only - no clinical details).
const FLAG_LABEL = {
  no_safety_plan: "No safety plan",
  not_seen_14_days: "Not seen in 14 days",
  no_next_session: "No next session booked",
};
const fmt = (iso) => (iso ? new Date(iso.length === 10 ? `${iso}T00:00:00` : iso).toLocaleDateString("en-IN", { day: "numeric", month: "short" }) : "-");

export default function RiskRegister() {
  const features = useFeatures();
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState("all");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!features.ready || !features.has("risk")) return;
    api.getRiskRegister().then((r) => setRows(r.data.rows || [])).catch((e) => setError(e?.response?.data?.error || "Couldn't load the risk register."));
  }, [features.ready]); // eslint-disable-line

  if (features.ready && !features.has("risk")) {
    return <DashboardLayout><div className="p-8 text-secondary-600">Risk assessments aren't switched on for your organisation yet.</div></DashboardLayout>;
  }

  const shown = (rows || []).filter((r) => filter === "all" || (filter === "needs" ? r.flags.length > 0 : r.level === filter));

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto space-y-5">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-secondary-900"><ShieldAlert className="w-6 h-6 text-rose-600" /> Risk register</h1>
          <p className="text-sm text-secondary-500">Patients currently at moderate or high risk, from their latest risk assessment.</p>
        </div>

        <div className="flex flex-wrap gap-2">
          {[["all", "All"], ["high", "High"], ["moderate", "Moderate"], ["needs", "Needs action"]].map(([k, label]) => (
            <button key={k} type="button" onClick={() => setFilter(k)}
              className={`rounded-full px-3 py-1.5 text-xs font-semibold border ${filter === k ? "bg-secondary-900 text-white border-secondary-900" : "bg-white text-secondary-700 border-secondary-300"}`}>
              {label}{rows ? ` (${(rows || []).filter((r) => k === "all" || (k === "needs" ? r.flags.length > 0 : r.level === k)).length})` : ""}
            </button>
          ))}
        </div>

        {error && <p className="text-sm text-red-600">{error}</p>}
        {!rows && !error && <p className="text-sm text-secondary-500">Loading…</p>}
        {rows && (
          <div className="rounded-xl bg-white border border-secondary-100 overflow-x-auto">
            <table className="w-full text-sm min-w-[720px]">
              <thead className="bg-secondary-50 text-xs uppercase tracking-wide text-secondary-500">
                <tr><th className="text-left px-4 py-2.5">Patient</th><th className="text-left px-4 py-2.5">Clinician</th><th className="text-left px-4 py-2.5">Risk</th><th className="text-left px-4 py-2.5">Assessed</th><th className="text-left px-4 py-2.5">Last seen</th><th className="text-left px-4 py-2.5">Next session</th><th className="text-left px-4 py-2.5">Needs</th></tr>
              </thead>
              <tbody>
                {shown.length === 0 && <tr><td colSpan={7} className="px-4 py-6 text-center text-secondary-500">{rows.length === 0 ? "No patients at moderate or high risk." : "Nothing in this view."}</td></tr>}
                {shown.map((r) => (
                  <tr key={r.patient_id} onClick={() => navigate(`/patient/${r.patient_id}?tab=risk`)} className="border-t border-secondary-100 cursor-pointer hover:bg-secondary-50">
                    <td className="px-4 py-3">
                      <p className="font-semibold text-secondary-900">{r.patient_name}</p>
                      <p className="text-xs text-secondary-500">{[r.age != null ? `${r.age} yrs` : null, r.gender, r.contact, `ID ${r.patient_id}`].filter(Boolean).join(" · ")}</p>
                    </td>
                    <td className="px-4 py-3 text-secondary-700">{r.primary_clinician || <span className="text-secondary-400">-</span>}</td>
                    <td className="px-4 py-3"><span className={`rounded-full px-2.5 py-0.5 text-xs font-bold capitalize ${r.level === "high" ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-800"}`}>{r.level}</span></td>
                    <td className="px-4 py-3 text-secondary-600">{fmt(r.assessed_at)}</td>
                    <td className="px-4 py-3 text-secondary-600">{fmt(r.last_seen)}</td>
                    <td className="px-4 py-3 text-secondary-600">{fmt(r.next_session)}</td>
                    <td className="px-4 py-3">
                      {r.flags.length === 0 ? <span className="text-xs text-emerald-700 font-semibold">All in place</span> : (
                        <div className="flex flex-wrap gap-1">
                          {r.flags.map((f) => <span key={f} className="rounded-full bg-rose-50 border border-rose-200 px-2 py-0.5 text-[11px] font-semibold text-rose-700">{FLAG_LABEL[f] || f}</span>)}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
